'use client'

import { Star } from 'lucide-react'
import Image from 'next/image'
import React, { useMemo, useState } from 'react'

import { CategoryFilterNav } from '@/components/CategoryFilterNav'
import { containerClassName } from '@/components/Container'
import RichText from '@/components/RichText'
import {
  amenityIconComponents,
  type AmenityIconComponent,
} from '@/lib/amenities/amenityIconOptions'
import { useMessages } from '@/lib/i18n/useMessages'
import type {
  LocationAmenities,
  LocationAmenity,
  LocationAmenityCategory,
  LocationAmenityIcon,
} from '@/lib/mock/locationPages'
import { hasLexicalContent } from '@/lib/richtext/lexicalToPlainText'
import { cn } from '@/utilities/ui'

type LocationAmenitiesSectionProps = {
  amenities: LocationAmenities
}

type AmenityFilterId = 'all' | LocationAmenityCategory

const amenityIcons: Record<LocationAmenityIcon, AmenityIconComponent> = amenityIconComponents

type AmenityCardProps = {
  amenity: LocationAmenity
}

function AmenityCard({ amenity }: AmenityCardProps) {
  const Icon = amenity.icon ? amenityIcons[amenity.icon] : null

  return (
    <article className="overflow-hidden rounded-xl border border-black/10 bg-white/50 p-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          {Icon ? <Icon aria-hidden className="size-5 shrink-0 text-dark-green" /> : null}
          <h3 className="font-outfit text-base font-bold leading-5 text-dark-800/95">{amenity.title}</h3>
        </div>

        {amenity.description && hasLexicalContent(amenity.description) ? (
          <RichText
            className="font-outfit text-sm leading-5 text-dark-800/80 [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5"
            data={amenity.description}
            enableGutter={false}
            enableProse={false}
          />
        ) : null}

        {amenity.image ? (
          <div className="relative mt-2 h-[13.75rem] w-full overflow-hidden rounded">
            <Image
              alt={amenity.imageAlt ?? ''}
              className="object-cover"
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              src={amenity.image}
            />
          </div>
        ) : null}
      </div>
    </article>
  )
}

export function LocationAmenitiesSection({ amenities }: LocationAmenitiesSectionProps) {
  const copy = useMessages().location.amenities
  const [activeFilter, setActiveFilter] = useState<AmenityFilterId>('all')

  const filterOrder = useMemo(
    () => amenities.filters.map((filter) => filter.id),
    [amenities.filters],
  )

  const filterLabels = useMemo(
    () => ({
      all: copy.filters.all,
      included: copy.filters.included,
      onDemand: copy.filters.onDemand,
    }),
    [copy.filters],
  )

  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') return amenities.items
    return amenities.items.filter((item) => item.category === activeFilter)
  }, [activeFilter, amenities.items])

  return (
    <section
      aria-labelledby="location-amenities-heading"
      className="relative overflow-hidden bg-cream"
    >
      <div
        className={cn(
          containerClassName,
          'relative z-10 lg:pt-20',
        )}
      >
        {amenities.decorativeImage || amenities.ratingValue ? (
          <div
            aria-hidden
            className="pointer-events-none absolute right-4 top-8 hidden items-end gap-2 lg:right-20 lg:top-16 lg:flex"
          >
            <div className="pb-6 text-center">
              <div className="flex items-center justify-center gap-1">
                <p className="font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-dark-800/80">
                  {amenities.ratingValue}
                </p>
                <Star
                  aria-hidden
                  className="size-[1.4375rem] shrink-0 fill-star text-star"
                />
              </div>
              <p className="mt-2 font-outfit text-base leading-4 text-dark-800/80">
                {copy.ratingLabel}
              </p>
            </div>
            {amenities.decorativeImage ? (
              <Image
                alt=""
                className="h-auto w-[11.5rem] object-contain"
                height={155}
                src={amenities.decorativeImage}
                width={184}
              />
            ) : null}
          </div>
        ) : null}

        <div className="max-w-[55rem]">
          <h2
            className="text-balance text-title font-semibold leading-none tracking-[-0.04em] text-bluetext"
            id="location-amenities-heading"
          >
            {copy.title}
          </h2>
          <p className="mt-10 max-w-[55rem] font-outfit text-base font-medium leading-5 text-dark-800/80">
            {copy.intro}
          </p>

          <CategoryFilterNav
            activeCategory={activeFilter}
            ariaLabel={copy.filterAriaLabel}
            categoryLabels={filterLabels}
            categoryOrder={filterOrder}
            className="mt-10"
            desktopAlign="start"
            desktopWidth="fit"
            layoutId="amenity-filter-indicator"
            onSelect={(filter) => setActiveFilter(filter as AmenityFilterId)}
          />
        </div>

        <ul className="mt-10 columns-1 gap-4 md:columns-2 lg:columns-3">
          {filteredItems.map((amenity) => (
            <li key={amenity.id} className="mb-4 break-inside-avoid">
              <AmenityCard amenity={amenity} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
